<template>
    <div class="page quiz-attempt-view-page">
        <div class="page-header">
            <div class="page-header-content">
                <h1>
                    查看作答詳情{{
                        attemptData?.quiz?.name
                            ? ` - ${attemptData.quiz.name}`
                            : ''
                    }}
                </h1>
                <a-button
                    type="text"
                    class="header-back-button"
                    @click="goBack"
                >
                    返回
                </a-button>
            </div>
        </div>

        <div v-if="!loading && attemptData" class="content-container">
            <a-row :gutter="24">
                <!-- 左側：基本資訊 -->
                <a-col :span="7">
                    <div class="info-card attempt-info-card">
                        <div class="card-title-wrapper">
                            <h3 class="card-title">
                                {{ attemptData.quiz?.name || '作答資訊' }}
                            </h3>
                            <a-tag
                                v-if="attemptData.quiz?.scoring_mode"
                                :color="
                                    getScoringModeColor(
                                        attemptData.quiz.scoring_mode
                                    )
                                "
                                class="mode-tag"
                            >
                                {{
                                    getScoringModeLabel(
                                        attemptData.quiz.scoring_mode
                                    )
                                }}
                            </a-tag>
                        </div>
                        <div class="info-content">
                            <div class="info-item">
                                <div class="info-label">作答者</div>
                                <div class="info-value">
                                    {{ attemptData.user_name || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">狀態</div>
                                <div class="info-value">
                                    <a-tag
                                        :color="
                                            getStatusColor(attemptData.status)
                                        "
                                    >
                                        {{
                                            attemptData.status_display ||
                                            attemptData.status
                                        }}
                                    </a-tag>
                                </div>
                            </div>

                            <div
                                v-if="
                                    isNormalMode(attemptData.quiz?.scoring_mode)
                                "
                                class="info-item"
                            >
                                <div class="info-label">分數</div>
                                <div class="info-value">
                                    {{
                                        attemptData.total_score !== null &&
                                        attemptData.total_score !== undefined
                                            ? `${attemptData.total_score} / ${
                                                  attemptData.max_score ||
                                                  attemptData.quiz
                                                      ?.total_score ||
                                                  '-'
                                              }`
                                            : '-'
                                    }}
                                    <a-tag
                                        v-if="
                                            attemptData.is_passed !== null &&
                                            attemptData.is_passed !== undefined
                                        "
                                        :color="
                                            attemptData.is_passed
                                                ? 'green'
                                                : 'red'
                                        "
                                        class="ml-2"
                                    >
                                        {{
                                            attemptData.is_passed
                                                ? '通過'
                                                : '未通過'
                                        }}
                                    </a-tag>
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">花費時間</div>
                                <div class="info-value">
                                    {{ attemptData.duration_display || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">開始時間</div>
                                <div class="info-value">
                                    {{ attemptData.started_at || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">提交時間</div>
                                <div class="info-value">
                                    {{ attemptData.submitted_at || '-' }}
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">最後更新時間</div>
                                <div class="info-value">
                                    {{ attemptData.updated_at || '-' }}
                                </div>
                            </div>

                            <div
                                v-if="attemptData.quiz?.description"
                                class="info-item"
                            >
                                <div class="info-label">測驗說明</div>
                                <div class="info-value description-value">
                                    {{ attemptData.quiz.description }}
                                </div>
                            </div>

                            <!-- 維度分數區塊 -->
                            <div
                                v-if="
                                    attemptData.quiz?.scoring_mode ===
                                        'dimensions' &&
                                    attemptData.quiz?.dimension_scores &&
                                    Array.isArray(
                                        attemptData.quiz.dimension_scores
                                    ) &&
                                    attemptData.quiz.dimension_scores.length > 0
                                "
                                class="dimension-scores-section"
                            >
                                <div class="dimension-scores-title">
                                    維度分數總覽
                                </div>
                                <div class="dimension-scores-list">
                                    <div
                                        v-for="dimensionScore in sortedDimensionScores"
                                        :key="dimensionScore.dimension_id"
                                        class="dimension-score-item"
                                        :class="{
                                            'dimension-score-rank-1':
                                                dimensionScore.rank_order === 1
                                        }"
                                    >
                                        <div class="dimension-score-header">
                                            <div class="dimension-score-name">
                                                {{
                                                    dimensionScore.dimension_name ||
                                                    `維度${dimensionScore.dimension_id}`
                                                }}
                                            </div>
                                            <div
                                                v-if="
                                                    dimensionScore.rank_order !==
                                                        null &&
                                                    dimensionScore.rank_order !==
                                                        undefined
                                                "
                                                class="dimension-score-rank"
                                            >
                                                第
                                                {{ dimensionScore.rank_order }}
                                                名
                                            </div>
                                        </div>
                                        <div class="dimension-score-values">
                                            <div class="dimension-score-raw">
                                                <span
                                                    class="dimension-score-label"
                                                >
                                                    原始分數
                                                </span>
                                                <span
                                                    class="dimension-score-number"
                                                >
                                                    {{
                                                        formatNumberValue(
                                                            dimensionScore.raw_score
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    dimensionScore.normalized_score !==
                                                        null &&
                                                    dimensionScore.normalized_score !==
                                                        undefined
                                                "
                                                class="dimension-score-normalized"
                                            >
                                                <span
                                                    class="dimension-score-label"
                                                >
                                                    規範化分數
                                                </span>
                                                <span
                                                    class="dimension-score-number"
                                                >
                                                    {{
                                                        formatNumberValue(
                                                            dimensionScore.normalized_score
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </a-col>

                <!-- 右側：作答內容 -->
                <a-col :span="17">
                    <!-- 結果卡區塊 -->
                    <div
                        v-if="
                            (attemptData.quiz?.scoring_mode === 'dimensions' ||
                                attemptData.quiz?.scoring_mode === '多維' ||
                                attemptData.quiz?.scoring_mode === '維度') &&
                            attemptData.result_card
                        "
                        class="result-card-section"
                    >
                        <div class="result-card-title">測驗結果</div>
                        <div class="result-card-content">
                            <!-- 結果卡圖片 -->
                            <div
                                v-if="attemptData.result_card?.image_url"
                                class="result-card-image-wrapper"
                            >
                                <img
                                    :src="attemptData.result_card.image_url"
                                    :alt="attemptData.result_card.title"
                                    class="result-card-image"
                                    @error="handleResultCardImageError"
                                />
                            </div>
                            <!-- 結果卡文字內容 -->
                            <div class="result-card-text">
                                <h4 class="result-card-title-text">
                                    {{ attemptData.result_card.title }}
                                </h4>
                                <p
                                    v-if="attemptData.result_card.subtitle"
                                    class="result-card-subtitle"
                                >
                                    {{ attemptData.result_card.subtitle }}
                                </p>
                                <p
                                    v-if="attemptData.result_card.summary"
                                    class="result-card-summary"
                                >
                                    {{ attemptData.result_card.summary }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="questions-container">
                        <div
                            v-if="
                                !attemptData.quiz?.questions ||
                                attemptData.quiz.questions.length === 0
                            "
                            class="empty-questions"
                        >
                            <div class="empty-icon">📝</div>
                            <div class="empty-text">尚無作答內容</div>
                        </div>
                        <div
                            v-else
                            v-for="(question, idx) in attemptData.quiz
                                .questions"
                            :key="question.id || idx"
                            class="question-card"
                        >
                            <div class="question-header">
                                <h3 class="question-title">
                                    題目 {{ idx + 1 }}
                                    <span
                                        v-if="
                                            isNormalMode(
                                                attemptData.quiz?.scoring_mode
                                            ) &&
                                            question?.max_score !== null &&
                                            question?.max_score !== undefined
                                        "
                                        class="question-score"
                                    >
                                        （得分：
                                        {{
                                            question?.user_answer?.score !==
                                                null &&
                                            question?.user_answer?.score !==
                                                undefined
                                                ? formatNumberValue(
                                                      question.user_answer.score
                                                  )
                                                : '0'
                                        }}
                                        /
                                        {{
                                            formatNumberValue(
                                                question.max_score
                                            )
                                        }}
                                        分）
                                    </span>
                                </h3>
                                <div class="question-header-right">
                                    <div class="question-tags">
                                        <a-tag
                                            :color="
                                                question?.question_type ===
                                                'single'
                                                    ? '#3b6ea5'
                                                    : question?.question_type ===
                                                        'multiple'
                                                      ? '#2a4a7a'
                                                      : '#64748b'
                                            "
                                        >
                                            {{
                                                getQuestionTypeLabel(
                                                    question?.question_type
                                                )
                                            }}
                                        </a-tag>
                                        <a-tag
                                            v-if="question?.is_required"
                                            color="#ff7875"
                                        >
                                            必填
                                        </a-tag>
                                        <!-- 人工審核狀態 -->
                                        <a-tag
                                            v-if="
                                                question?.scoring_mode ===
                                                'manual'
                                            "
                                            :color="
                                                question?.user_answer?.review
                                                    ?.status === 1
                                                    ? 'green'
                                                    : 'orange'
                                            "
                                        >
                                            {{
                                                question?.user_answer?.review
                                                    ?.status === 1
                                                    ? '已審核'
                                                    : '未審核'
                                            }}
                                        </a-tag>
                                    </div>
                                </div>
                            </div>

                            <div class="question-body">
                                <!-- 人工審核資訊 -->
                                <div
                                    v-if="
                                        question?.scoring_mode === 'manual' &&
                                        question?.user_answer?.review
                                    "
                                    class="review-info-section"
                                >
                                    <div class="review-info-item">
                                        <div class="review-label">審核狀態</div>
                                        <div class="review-value">
                                            <a-tag
                                                :color="
                                                    question.user_answer.review
                                                        .status === 1
                                                        ? 'green'
                                                        : 'orange'
                                                "
                                            >
                                                {{
                                                    question.user_answer.review
                                                        .status === 1
                                                        ? '已審核'
                                                        : '未審核'
                                                }}
                                            </a-tag>
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            question.user_answer.score !==
                                                null &&
                                            question.user_answer.score !==
                                                undefined
                                        "
                                        class="review-info-item"
                                    >
                                        <div class="review-label">得分</div>
                                        <div class="review-value score-value">
                                            {{
                                                formatNumberValue(
                                                    question.user_answer.score
                                                )
                                            }}
                                            /
                                            {{
                                                formatNumberValue(
                                                    question.max_score
                                                )
                                            }}
                                            分
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            question.user_answer.review
                                                .reviewed_at
                                        "
                                        class="review-info-item"
                                    >
                                        <div class="review-label">審核時間</div>
                                        <div class="review-value">
                                            {{
                                                question.user_answer.review
                                                    .reviewed_at
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            question.user_answer.review.comment
                                        "
                                        class="review-info-item review-comment"
                                    >
                                        <div class="review-label">審核備註</div>
                                        <div class="review-value">
                                            {{
                                                question.user_answer.review
                                                    .comment
                                            }}
                                        </div>
                                    </div>
                                </div>
                                <div class="question-content-row">
                                    <div class="question-field">
                                        <div class="field-label">問題</div>
                                        <div class="field-value">
                                            {{ question?.title || '-' }}
                                        </div>
                                    </div>

                                    <div
                                        v-if="question?.description"
                                        class="question-field"
                                    >
                                        <div class="field-label">說明</div>
                                        <div class="field-value">
                                            {{ question.description }}
                                        </div>
                                    </div>
                                </div>

                                <!-- 選項題（單選、多選） -->
                                <div
                                    v-if="
                                        question?.question_type === 'single' ||
                                        question?.question_type === 'multiple'
                                    "
                                    class="options-section"
                                >
                                    <div class="options-title">選項</div>
                                    <div class="options-list">
                                        <div
                                            v-for="option in question?.options ||
                                            []"
                                            :key="option.id"
                                            class="option-item"
                                            :class="{
                                                'option-selected-correct':
                                                    isNormalMode(
                                                        attemptData.quiz
                                                            ?.scoring_mode
                                                    ) &&
                                                    shouldShowCorrectness(
                                                        question
                                                    ) &&
                                                    !question?.user_answer
                                                        ?.review &&
                                                    isOptionSelected(
                                                        question,
                                                        option
                                                    ) &&
                                                    isCorrectOption(option),
                                                'option-selected-wrong':
                                                    isNormalMode(
                                                        attemptData.quiz
                                                            ?.scoring_mode
                                                    ) &&
                                                    shouldShowCorrectness(
                                                        question
                                                    ) &&
                                                    !question?.user_answer
                                                        ?.review &&
                                                    isOptionSelected(
                                                        question,
                                                        option
                                                    ) &&
                                                    !isCorrectOption(option),
                                                'option-missed-correct':
                                                    isNormalMode(
                                                        attemptData.quiz
                                                            ?.scoring_mode
                                                    ) &&
                                                    shouldShowCorrectness(
                                                        question
                                                    ) &&
                                                    !question?.user_answer
                                                        ?.review &&
                                                    !isOptionSelected(
                                                        question,
                                                        option
                                                    ) &&
                                                    isCorrectOption(option),
                                                'option-selected-survey':
                                                    attemptData.quiz
                                                        ?.scoring_mode ===
                                                        'survey' &&
                                                    isOptionSelected(
                                                        question,
                                                        option
                                                    ),
                                                'option-selected-dimensions':
                                                    attemptData.quiz
                                                        ?.scoring_mode ===
                                                        'dimensions' &&
                                                    isOptionSelected(
                                                        question,
                                                        option
                                                    )
                                            }"
                                        >
                                            <div
                                                v-if="
                                                    attemptData.quiz
                                                        ?.scoring_mode !==
                                                    'dimensions'
                                                "
                                                class="option-indicator"
                                            >
                                                <div
                                                    class="option-shape"
                                                    :class="{
                                                        'shape-circle':
                                                            question?.question_type ===
                                                            'single',
                                                        'shape-square':
                                                            question?.question_type ===
                                                            'multiple',
                                                        'shape-selected':
                                                            isOptionSelected(
                                                                question,
                                                                option
                                                            )
                                                    }"
                                                >
                                                    <span
                                                        v-if="
                                                            isOptionSelected(
                                                                question,
                                                                option
                                                            )
                                                        "
                                                        class="shape-check"
                                                    >
                                                        ✓
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="option-main">
                                                <div class="option-label">
                                                    {{ option?.label || '' }}
                                                    <span
                                                        v-if="
                                                            isNormalMode(
                                                                attemptData.quiz
                                                                    ?.scoring_mode
                                                            ) &&
                                                            option?.score !==
                                                                null &&
                                                            option?.score !==
                                                                undefined
                                                        "
                                                        class="option-score"
                                                    >
                                                        （{{
                                                            formatNumberValue(
                                                                option.score
                                                            )
                                                        }}
                                                        分）
                                                    </span>
                                                    <span
                                                        v-if="
                                                            isNormalMode(
                                                                attemptData.quiz
                                                                    ?.scoring_mode
                                                            ) &&
                                                            shouldShowCorrectness(
                                                                question
                                                            ) &&
                                                            !question
                                                                ?.user_answer
                                                                ?.review &&
                                                            isCorrectOption(
                                                                option
                                                            )
                                                        "
                                                        class="correct-checkmark"
                                                    >
                                                        ✓ 正確答案
                                                    </span>
                                                    <span
                                                        v-else-if="
                                                            isNormalMode(
                                                                attemptData.quiz
                                                                    ?.scoring_mode
                                                            ) &&
                                                            shouldShowCorrectness(
                                                                question
                                                            ) &&
                                                            !question
                                                                ?.user_answer
                                                                ?.review &&
                                                            isOptionSelected(
                                                                question,
                                                                option
                                                            ) &&
                                                            !isCorrectOption(
                                                                option
                                                            )
                                                        "
                                                        class="wrong-checkmark"
                                                    >
                                                        ✗ 錯誤答案
                                                    </span>
                                                </div>
                                                <!-- 維度類型：顯示維度加權 -->
                                                <div
                                                    v-if="
                                                        attemptData.quiz
                                                            ?.scoring_mode ===
                                                            'dimensions' &&
                                                        attemptData.quiz
                                                            ?.dimensions &&
                                                        attemptData.quiz
                                                            .dimensions.length >
                                                            0 &&
                                                        option?.weights &&
                                                        Array.isArray(
                                                            option.weights
                                                        ) &&
                                                        option.weights.length >
                                                            0
                                                    "
                                                    class="option-dimension-weights"
                                                >
                                                    <div
                                                        v-for="weightItem in option.weights"
                                                        :key="
                                                            weightItem.dimension_id ||
                                                            weightItem.dimension_name
                                                        "
                                                        class="dimension-weight-item"
                                                    >
                                                        <span
                                                            class="dimension-weight-label"
                                                        >
                                                            {{
                                                                weightItem.dimension_name ||
                                                                `維度${weightItem.dimension_id}`
                                                            }}
                                                        </span>
                                                        <span
                                                            class="dimension-weight-value"
                                                        >
                                                            {{
                                                                formatNumberValue(
                                                                    weightItem.weight
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- 問卷模式未作答提醒 -->
                                    <div
                                        v-if="
                                            attemptData.quiz?.scoring_mode ===
                                                'survey' &&
                                            !hasAnswered(question) &&
                                            question?.is_required
                                        "
                                        class="survey-not-answered-warning"
                                    >
                                        <a-tag color="red">
                                            未作答（必填）
                                        </a-tag>
                                    </div>
                                </div>

                                <!-- 文字題 -->
                                <div
                                    v-else-if="
                                        question?.question_type === 'text' ||
                                        question?.question_type === 'long_text'
                                    "
                                    class="text-answer-section"
                                >
                                    <div
                                        v-if="
                                            attemptData.quiz?.scoring_mode ===
                                            'survey'
                                        "
                                        class="user-answer"
                                        :class="{
                                            'user-answer-empty':
                                                !hasAnswered(question) &&
                                                question?.is_required
                                        }"
                                    >
                                        <div class="answer-label">
                                            使用者作答：
                                        </div>
                                        <div class="answer-value">
                                            {{
                                                getUserAnswerValue(question) ||
                                                '-'
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-else
                                        class="user-answer"
                                        :class="{
                                            'user-answer-wrong':
                                                shouldShowCorrectness(
                                                    question
                                                ) &&
                                                isTextAnswerWrong(question),
                                            'user-answer-correct':
                                                shouldShowCorrectness(
                                                    question
                                                ) &&
                                                isTextAnswerCorrect(question)
                                        }"
                                    >
                                        <div class="answer-label">
                                            使用者作答：
                                        </div>
                                        <div class="answer-value">
                                            {{
                                                getUserAnswerValue(question) ||
                                                '-'
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            isNormalMode(
                                                attemptData.quiz?.scoring_mode
                                            ) &&
                                            shouldShowCorrectness(question) &&
                                            question?.correct_answer &&
                                            !question?.user_answer?.review
                                        "
                                        class="correct-answer"
                                    >
                                        <div class="answer-label">
                                            正確答案：
                                        </div>
                                        <div class="answer-value correct">
                                            {{ question.correct_answer }}
                                        </div>
                                    </div>
                                    <!-- 問卷模式未作答提醒 -->
                                    <div
                                        v-if="
                                            attemptData.quiz?.scoring_mode ===
                                                'survey' &&
                                            !hasAnswered(question) &&
                                            question?.is_required
                                        "
                                        class="survey-not-answered-warning"
                                    >
                                        <a-tag color="red">
                                            未作答（必填）
                                        </a-tag>
                                    </div>
                                </div>

                                <!-- 數字題 -->
                                <div
                                    v-else-if="
                                        question?.question_type === 'number'
                                    "
                                    class="number-answer-section"
                                >
                                    <div
                                        v-if="
                                            attemptData.quiz?.scoring_mode ===
                                            'survey'
                                        "
                                        class="user-answer"
                                        :class="{
                                            'user-answer-empty':
                                                !hasAnswered(question) &&
                                                question?.is_required
                                        }"
                                    >
                                        <div class="answer-label">
                                            使用者作答：
                                        </div>
                                        <div class="answer-value">
                                            {{
                                                getUserAnswerValue(question) ||
                                                '-'
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-else
                                        class="user-answer"
                                        :class="{
                                            'user-answer-wrong':
                                                shouldShowCorrectness(
                                                    question
                                                ) &&
                                                isNumberAnswerWrong(question),
                                            'user-answer-correct':
                                                shouldShowCorrectness(
                                                    question
                                                ) &&
                                                isNumberAnswerCorrect(question)
                                        }"
                                    >
                                        <div class="answer-label">
                                            使用者作答：
                                        </div>
                                        <div class="answer-value">
                                            {{
                                                getUserAnswerValue(question) ||
                                                '-'
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            isNormalMode(
                                                attemptData.quiz?.scoring_mode
                                            ) &&
                                            shouldShowCorrectness(question) &&
                                            question?.correct_answer &&
                                            !question?.user_answer?.review
                                        "
                                        class="correct-answer"
                                    >
                                        <div class="answer-label">
                                            正確答案：
                                        </div>
                                        <div class="answer-value correct">
                                            {{
                                                formatNumberValue(
                                                    question.correct_answer
                                                )
                                            }}
                                        </div>
                                    </div>
                                    <!-- 問卷模式未作答提醒 -->
                                    <div
                                        v-if="
                                            attemptData.quiz?.scoring_mode ===
                                                'survey' &&
                                            !hasAnswered(question) &&
                                            question?.is_required
                                        "
                                        class="survey-not-answered-warning"
                                    >
                                        <a-tag color="red">
                                            未作答（必填）
                                        </a-tag>
                                    </div>
                                </div>

                                <!-- 問卷模式：顯示未作答提醒 -->
                                <div
                                    v-if="
                                        attemptData.quiz?.scoring_mode ===
                                            'survey' &&
                                        !hasAnswered(question) &&
                                        question?.is_required
                                    "
                                    class="survey-answer-info"
                                >
                                    <a-tag color="red">未作答（必填）</a-tag>
                                </div>
                            </div>
                        </div>
                    </div>
                </a-col>
            </a-row>
        </div>

        <div v-if="loading" class="loading-container">
            <a-spin size="large" />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { getQuizAttemptDetail } from '@/api/quizzes';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();
const { params } = route;
const { id } = params;
const loading = ref(false);

const attemptData = ref(null);

// 獲取狀態顏色
const getStatusColor = (status) => {
    const colorMap = {
        in_progress: 'orange',
        submitted: 'blue',
        reviewing: 'purple',
        completed: 'green',
        passed: 'green',
        failed: 'red'
    };
    return colorMap[status] || 'default';
};

// 獲取計分模式標籤
const getScoringModeLabel = (scoringMode) => {
    const labelMap = {
        normal: '測驗',
        dimensions: '多維',
        survey: '問卷'
    };
    return labelMap[scoringMode] || scoringMode;
};

// 獲取計分模式顏色（莫蘭迪色系）
const getScoringModeColor = (scoringMode) => {
    // Morandi 色系：莫蘭迪藍、莫蘭迪黃、莫蘭迪綠
    // 同時支援英文和中文格式
    const colorMap = {
        normal: '#3b6ea5', // Morandi 藍（測驗）
        '測驗': '#3b6ea5',
        dimensions: '#B8A05A', // Morandi 黃（多維）
        '多維': '#B8A05A',
        '維度': '#B8A05A',
        survey: '#9BB7D4', // Morandi 綠（問卷）
        '問卷': '#9BB7D4'
    };
    return colorMap[scoringMode] || '#0e7490';
};

// 獲取題目類型標籤
const getQuestionTypeLabel = (questionType) => {
    const labelMap = {
        single: '單選題',
        multiple: '多選題',
        text: '短文字',
        long_text: '長文字',
        number: '數字',
        scale: '量表'
    };
    return labelMap[questionType] || questionType || '-';
};

// 判斷選項是否被使用者選中
const isOptionSelected = (question, option) => {
    if (!question || !option || !question.user_answer) return false;

    // user_answer.value 是選項 ID（單選）或選項 ID 陣列（多選）
    const userValue = question.user_answer.value;

    if (Array.isArray(userValue)) {
        return userValue.some(
            (val) => val === option.id || Number(val) === option.id
        );
    }

    // 單選題：直接比較 value 和 option.id
    return userValue === option.id || Number(userValue) === option.id;
};

// 判斷選項是否為正確答案（根據 option.score > 0）
const isCorrectOption = (option) => {
    if (!option) return false;
    // 正確答案應該是分數 > 0 的選項
    if (option.score === null || option.score === undefined) return false;
    // 將分數轉換為數字進行比較（因為 API 回傳的是字串）
    const score = parseFloat(option.score);
    return !isNaN(score) && score > 0;
};

// 判斷是否為測驗模式（normal）
const isNormalMode = (scoringMode) => {
    return scoringMode === 'normal' || scoringMode === '測驗';
};

// 判斷是否應該顯示正確/錯誤判斷（僅在 normal 模式下顯示，人工審核模式且未審核時不顯示）
const shouldShowCorrectness = (question) => {
    // 只有 normal 模式才顯示正確/錯誤答案
    if (!isNormalMode(attemptData.value?.quiz?.scoring_mode)) {
        return false;
    }
    // 如果是人工審核模式且未審核，不顯示正確/錯誤
    if (question?.scoring_mode === 'manual') {
        const reviewStatus = question?.user_answer?.review?.status;
        if (
            reviewStatus === 0 ||
            reviewStatus === null ||
            reviewStatus === undefined
        ) {
            return false;
        }
    }
    // normal 模式且已審核（或非人工審核模式）正常顯示
    return true;
};

// 格式化數字值，移除多餘的小數點 0
const formatNumberValue = (value) => {
    if (value === null || value === undefined || value === '') return value;
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
        // 如果是整數，直接顯示整數；否則顯示小數但去掉尾數的 0
        return numValue % 1 === 0 ? numValue.toString() : numValue.toString();
    }
    return value;
};

// 取得使用者作答的內容（用於文字題和數字題）
const getUserAnswerValue = (question) => {
    if (!question || !question.user_answer) return null;
    const value = question.user_answer.value;

    // 如果是數字題，移除多餘的小數點 0
    if (question.question_type === 'number') {
        return formatNumberValue(value);
    }

    return value;
};

// 判斷文字題作答是否正確
const isTextAnswerCorrect = (question) => {
    if (!question || !question.user_answer || !question.correct_answer)
        return false;
    if (attemptData.value?.quiz?.scoring_mode === 'survey') return false;

    const userValue = String(question.user_answer.value || '').trim();
    const correctValue = String(question.correct_answer || '').trim();

    return userValue === correctValue;
};

// 處理結果卡圖片載入錯誤
const handleResultCardImageError = () => {
    // 圖片載入失敗時不做任何處理，使用 API 返回的 image_url 即可
};

// 排序維度分數（由高到低）
const sortedDimensionScores = computed(() => {
    if (
        !attemptData.value?.quiz?.dimension_scores ||
        !Array.isArray(attemptData.value.quiz.dimension_scores)
    ) {
        return [];
    }

    const scores = [...attemptData.value.quiz.dimension_scores];

    // 按照 raw_score 由高到低排序，如果相同則按照 rank_order 排序
    return scores.sort((a, b) => {
        const scoreA = parseFloat(a.raw_score) || 0;
        const scoreB = parseFloat(b.raw_score) || 0;

        if (scoreB !== scoreA) {
            return scoreB - scoreA; // 由高到低
        }

        // 如果分數相同，按照 rank_order 排序
        const rankA =
            a.rank_order !== null && a.rank_order !== undefined
                ? Number(a.rank_order)
                : 999;
        const rankB =
            b.rank_order !== null && b.rank_order !== undefined
                ? Number(b.rank_order)
                : 999;

        return rankA - rankB;
    });
});

// 判斷文字題作答是否錯誤
const isTextAnswerWrong = (question) => {
    if (!question || !question.user_answer || !question.correct_answer)
        return false;
    if (attemptData.value?.quiz?.scoring_mode === 'survey') return false;

    return !isTextAnswerCorrect(question);
};

// 判斷數字題作答是否正確
const isNumberAnswerCorrect = (question) => {
    if (!question || !question.user_answer || !question.correct_answer)
        return false;
    if (attemptData.value?.quiz?.scoring_mode === 'survey') return false;

    const userValue = parseFloat(question.user_answer.value);
    const correctValue = parseFloat(question.correct_answer);

    if (isNaN(userValue) || isNaN(correctValue)) return false;

    return userValue === correctValue;
};

// 判斷數字題作答是否錯誤
const isNumberAnswerWrong = (question) => {
    if (!question || !question.user_answer || !question.correct_answer)
        return false;
    if (attemptData.value?.quiz?.scoring_mode === 'survey') return false;

    return !isNumberAnswerCorrect(question);
};

// 判斷使用者是否有作答
const hasAnswered = (question) => {
    if (!question) return false;

    // 選項題：檢查 user_answer.value
    if (
        question.question_type === 'single' ||
        question.question_type === 'multiple'
    ) {
        if (question.user_answer && question.user_answer.value) {
            if (Array.isArray(question.user_answer.value)) {
                return question.user_answer.value.length > 0;
            }
            return (
                question.user_answer.value !== null &&
                question.user_answer.value !== undefined &&
                question.user_answer.value !== ''
            );
        }
        return false;
    }

    // 文字題和數字題：檢查 user_answer.value
    if (
        question.question_type === 'text' ||
        question.question_type === 'long_text' ||
        question.question_type === 'number'
    ) {
        if (question.user_answer && question.user_answer.value) {
            const value = String(question.user_answer.value).trim();
            return value !== '' && value !== '-';
        }
        return false;
    }

    return false;
};

// 返回按鈕處理
const goBack = () => {
    router.push({
        name: 'QuizzesAttempts'
    });
};

// 載入作答詳情
const loadAttemptDetail = async () => {
    try {
        loading.value = true;

        const response = await getQuizAttemptDetail(id);

        if (response.result || response.success) {
            attemptData.value = response.data;
            // 如果有結果卡，載入圖片
            if (
                (attemptData.value?.quiz?.scoring_mode === 'dimensions' ||
                    attemptData.value?.quiz?.scoring_mode === '多維' ||
                    attemptData.value?.quiz?.scoring_mode === '維度') &&
                attemptData.value?.result_card?.id
            ) {
                // 結果卡圖片直接使用 API 返回的 image_url，不需要額外載入
            }
        } else {
            showErrorMessage(response.message || '載入失敗');
            goBack();
        }
    } catch (error) {
        console.error('載入作答詳情失敗:', error);
        showErrorMessage('載入作答詳情失敗');
        goBack();
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadAttemptDetail();
});

// 元件卸載時清理 blob URL
onBeforeUnmount(() => {});
</script>

<style scoped>
.quiz-attempt-view-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 32px;
}

.page-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-header h1 {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3);
}

.header-back-button {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    color: rgba(42, 42, 58, 0.85) !important;
    transition: all 0.3s ease !important;
}

.header-back-button:hover {
    color: #3b6ea5 !important;
    transform: translateX(-4px);
}

.content-container {
    position: relative;
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 100px 0;
}

.info-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.2);
    border: 2px solid transparent;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)),
        linear-gradient(
            45deg,
            rgba(255, 158, 207, 0.3),
            rgba(42, 74, 122, 0.3),
            rgba(138, 175, 255, 0.3)
        );
    background-origin: border-box;
    background-clip: padding-box, border-box;
    position: sticky;
    top: 24px;
}

.card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.3);
}

.card-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

/* 計分模式 tag 樣式（統一） */
.scoring-mode-tag {
    border: none !important;
    font-family: 'Courier New', monospace !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    padding: 2px 8px !important;
    height: 20px !important;
    line-height: 16px !important;
    border-radius: 4px !important;
    margin-right: 8px !important;
}

.info-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-label {
    font-size: 12px;
    color: #6a6a7a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.info-value {
    font-size: 14px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    word-break: break-word;
}

.description-value {
    line-height: 1.6;
}

.questions-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.question-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.2);
    border: 2px solid transparent;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)),
        linear-gradient(
            45deg,
            rgba(255, 158, 207, 0.3),
            rgba(42, 74, 122, 0.3),
            rgba(138, 175, 255, 0.3)
        );
    background-origin: border-box;
    background-clip: padding-box, border-box;
}

.question-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
}

.question-title {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.question-score {
    font-size: 14px;
    font-weight: 500;
    color: #6a6a7a;
    margin-left: 8px;
}

.question-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
}

.question-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.question-body {
    margin-top: 20px;
}

.question-content-row {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
}

.question-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 12px;
    color: #6a6a7a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.field-value {
    font-size: 16px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    line-height: 1.6;
}

.options-section {
    margin-top: 20px;
}

.options-title {
    font-size: 14px;
    font-weight: 600;
    color: #2a2a3a;
    margin-bottom: 16px;
    font-family: 'Courier New', monospace;
}

.options-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.option-item {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid rgba(168, 255, 246, 0.3);
    background: rgba(255, 255, 255, 0.5);
    transition: all 0.3s ease;
}

.option-item.option-selected-correct {
    background: rgba(82, 196, 26, 0.15);
    border-color: #52c41a;
}

.option-item.option-selected-wrong {
    background: rgba(255, 77, 79, 0.15);
    border-color: #ff4d4f;
}

.option-item.option-missed-correct {
    background: rgba(255, 77, 79, 0.15);
    border-color: #ff4d4f;
}

.option-item.option-selected-survey {
    background: rgba(138, 175, 255, 0.15);
    border-color: #3b6ea5;
}

.option-item.option-selected-dimensions {
    background: rgba(168, 255, 246, 0.15);
    border-color: #0e7490;
}

.option-indicator {
    margin-right: 12px;
    flex-shrink: 0;
}

.option-shape {
    width: 20px;
    height: 20px;
    border: 2px solid #3b6ea5;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.shape-circle {
    border-radius: 50%;
}

.shape-square {
    border-radius: 4px;
}

.shape-selected {
    background: #2a4a7a;
    border-color: #2a4a7a;
}

.shape-check {
    color: white;
    font-size: 12px;
    font-weight: bold;
}

.option-main {
    flex: 1;
}

.option-label {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    line-height: 1.6;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.option-score {
    font-size: 13px;
    color: #3b6ea5;
    font-weight: 600;
}

.correct-checkmark {
    margin-left: 8px;
    color: #52c41a;
    font-weight: 600;
}

.wrong-checkmark {
    margin-left: 8px;
    color: #ff4d4f;
    font-weight: 600;
}

.text-answer-section,
.number-answer-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 20px;
}

.user-answer,
.correct-answer {
    padding: 16px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.7);
}

.user-answer {
    border: 1px solid rgba(138, 175, 255, 0.3);
}

.user-answer-correct {
    border: 1px solid rgba(82, 196, 26, 0.3);
    background: rgba(82, 196, 26, 0.05);
}

.user-answer-wrong {
    border: 2px solid rgba(255, 77, 79, 0.5);
    background: rgba(255, 77, 79, 0.08);
}

.user-answer-empty {
    border: 2px solid rgba(255, 77, 79, 0.5);
    background: rgba(255, 77, 79, 0.08);
}

.survey-not-answered-warning {
    margin-top: 12px;
    padding: 8px;
}

.correct-answer {
    border: 1px solid rgba(82, 196, 26, 0.3);
    background: rgba(82, 196, 26, 0.05);
}

.answer-label {
    font-size: 12px;
    font-weight: 600;
    color: #6a6a7a;
    margin-bottom: 8px;
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.answer-value {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    padding: 12px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    line-height: 1.6;
}

.answer-value.correct {
    color: #52c41a;
    font-weight: 600;
}

.survey-answer-info {
    margin-top: 16px;
    padding: 12px;
}

.empty-questions {
    text-align: center;
    padding: 80px 20px;
    color: #6a6a7a;
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.empty-text {
    font-family: 'Courier New', monospace;
    font-size: 16px;
}

/* 人工審核資訊樣式 */
.review-info-section {
    margin-bottom: 20px;
    padding: 16px;
    background: rgba(168, 255, 246, 0.1);
    border: 1px solid rgba(168, 255, 246, 0.3);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.review-info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.review-info-item.review-comment {
    flex-direction: column;
    align-items: flex-start;
}

.review-label {
    font-size: 12px;
    color: #6a6a7a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 80px;
    flex-shrink: 0;
}

.review-value {
    font-size: 14px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    word-break: break-word;
    flex: 1;
}

.review-value.score-value {
    font-weight: 700;
    color: #2a4a7a;
    font-size: 16px;
}

.review-value .ant-tag {
    margin: 0;
}

/* 維度加權樣式 */
.option-dimension-weights {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(168, 255, 246, 0.3);
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.dimension-weight-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    background: rgba(42, 74, 122, 0.1);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 6px;
    font-family: 'Courier New', monospace;
}

.dimension-weight-label {
    font-size: 12px;
    color: #6a6a7a;
    font-weight: 600;
}

.dimension-weight-value {
    font-size: 13px;
    color: #2a4a7a;
    font-weight: 700;
}

/* 維度分數總覽樣式 */
.dimension-scores-section {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 2px solid rgba(42, 74, 122, 0.3);
}

/* 結果卡樣式 */
.result-card-section {
    margin-bottom: 32px;
}

.result-card-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 16px;
    font-family: 'Courier New', monospace;
}

.result-card-content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    border: 2px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(42, 74, 122, 0.1);
    transition: all 0.3s ease;
}

.result-card-content:hover {
    border-color: rgba(42, 74, 122, 0.5);
    box-shadow: 0 6px 30px rgba(42, 74, 122, 0.2);
}

.result-card-image-wrapper {
    width: 100%;
    margin-bottom: 20px;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(168, 255, 246, 0.3);
}

.result-card-image {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    max-height: 400px;
    max-width: 100%;
}

.result-card-text {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.result-card-title-text {
    font-size: 20px;
    font-weight: 700;
    color: #2a2a3a;
    margin: 0;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3);
}

.result-card-subtitle {
    font-size: 16px;
    color: #6a6a7a;
    margin: 0;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    line-height: 1.6;
}

.result-card-summary {
    font-size: 14px;
    color: #4a4a5a;
    margin: 0;
    font-family: 'Courier New', monospace;
    line-height: 1.8;
    white-space: pre-wrap;
}

.dimension-scores-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 16px;
    font-family: 'Courier New', monospace;
}

.dimension-scores-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dimension-score-item {
    padding: 16px;
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(168, 255, 246, 0.3);
    border-radius: 8px;
    transition: all 0.3s ease;
}

.dimension-score-item.dimension-score-rank-1 {
    background: rgba(42, 74, 122, 0.15);
    border-color: #2a4a7a;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2);
}

.dimension-score-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.dimension-score-name {
    font-size: 15px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.dimension-score-rank {
    font-size: 12px;
    color: #2a4a7a;
    font-weight: 600;
    padding: 4px 10px;
    background: rgba(42, 74, 122, 0.1);
    border-radius: 12px;
    font-family: 'Courier New', monospace;
}

.dimension-score-item.dimension-score-rank-1 .dimension-score-rank {
    background: rgba(42, 74, 122, 0.3);
    color: #2a4a7a;
    font-weight: 700;
}

.dimension-score-values {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dimension-score-raw,
.dimension-score-normalized {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.dimension-score-label {
    font-size: 13px;
    color: #6a6a7a;
    font-weight: 500;
}

.dimension-score-number {
    font-size: 16px;
    font-weight: 700;
    color: #2a4a7a;
    font-family: 'Courier New', monospace;
}

.dimension-score-item.dimension-score-rank-1 .dimension-score-number {
    color: #2a4a7a;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.4);
}
</style>
